import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { LecturerData } from '../entities/lecturerData.js';
import {
  getCurrentUserServer,
  getCurrentUserServerOrFail,
  loadRawEntityDataByID,
  loadRawEntityDataByQuery,
} from '@adornis/bh-util/util.js';
import { merge, of, switchMap, throttleTime } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Product } from '../entities/products.js';
import { Teaching } from '../entities/teaching.js';
import { User } from '../entities/user.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { LecturerDataview } from '../use-case-entities/LecturerDataview.js';
import { withIdTiebreaker } from '../util.js';

function lecturerDataviewAggregation(filter: BHFilter, institutionID: string) {
  // The semester scopes workshop titles, not the lecturers themselves.
  const { timeframeID, ...lecturerFilter } = filter.getMongoFilter();
  return [
    {
      $match: {
        roles: { $elemMatch: { name: 'lecturer', contexts: { $in: [institutionID] } } },
      },
    },
    { $lookup: { from: 'teachings', localField: '_id', foreignField: 'lecturerID', as: '_teachings' } },
    {
      $lookup: {
        from: Product._collectionName,
        localField: '_teachings.productID',
        foreignField: '_id',
        pipeline: [{ $match: { institutionID, ...(timeframeID ? { timeframeID } : {}) } }],
        as: 'products',
      },
    },
    { $set: { user: '$$ROOT' } },
    { $unset: ['user._teachings', 'user.products'] },
    { $set: { institutionID } },
    { $match: lecturerFilter },
  ];
}

export const subscribeLecturerDataview = registerSubscription({
  operationName: 'subscribeLecturerDataview',
  type: () => [LecturerDataview],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],

  resolve: (skip: Int, limit: Int, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<LecturerDataview>) => {
    const institutionID = filter.institutionID;
    if (!institutionID) {
      logger.warn('No institutionID in filter');
      return of([]);
    }

    const baseAggregation = [
      ...lecturerDataviewAggregation(filter, institutionID),
      { $addFields: { _class: LecturerDataview._class } },
    ];

    return merge(
      watchEntireCollection(User._collectionName),
      watchEntireCollection(Teaching._collectionName),
      watchEntireCollection(Product._collectionName),
    ).pipe(
      throttleTime(500, undefined, { leading: true, trailing: true }),
      switchMap(() =>
        (async () => {
          const user = await getCurrentUserServerOrFail();
          if (!user.hasPermission(Permission.EDIT_LECTURERS, institutionID)) {
            throw new Error('unauthorized');
          }
          const aggregation = [
            ...baseAggregation,
            { $project: { ...LecturerDataview.neededGqlFields, products: 1 } },
            { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
            { $skip: skip },
            { $limit: limit },
          ];
          const collection = mongo().collection<User>(User._collectionName);
          return collection.raw.aggregate<LecturerDataview>(aggregation).toArray();
        })(),
      ),
    );
  },
});

/* Calculate the total quantity of all lecturers of an institution */
export const subscribeLecturerDataviewCount = registerSubscription({
  operationName: 'subscribeLecturerDataviewCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],

  resolve: (filter: BHFilter) => () => {
    const institutionID = filter.institutionID;
    if (!institutionID) {
      logger.warn('No institutionID in filter');
      return of(0);
    }

    return merge(
      watchEntireCollection(User._collectionName),
      watchEntireCollection(Teaching._collectionName),
      watchEntireCollection(Product._collectionName),
    ).pipe(
      throttleTime(500, undefined, { leading: true, trailing: true }),
      switchMap(() =>
        (async () => {
          const user = await getCurrentUserServerOrFail();
          if (!user.hasPermission(Permission.EDIT_LECTURERS, institutionID)) {
            throw new Error('unauthorized');
          }
          const aggregation = [
            ...lecturerDataviewAggregation(filter, institutionID),
            { $count: 'count' },
          ];
          const collection = mongo().collection<User>(User._collectionName);
          const result = await collection.raw.aggregate<{ count: number }>(aggregation).toArray();
          return result[0]?.count ?? 0;
        })(),
      ),
    );
  },
});

export const getLecturerDataByLecturerID = registerQuery({
  operationName: 'getLecturerDataByID',
  type: () => LecturerData,
  params: [{ name: 'lecturerID', type: () => String }],
  resolve: (lecturerID: string) => {
    return async (gqlFields: BaseQLSelectionSet<LecturerData>) => {
      gqlFields._id = 1;
      gqlFields._class = 1;
      const user = await getCurrentUserServer();
      if (!user?.hasPermission(Permission.EDIT_LECTURERS) && user?._id !== lecturerID)
        throw new Error('You dont have enough permission!');
      return (await loadRawEntityDataByID(LecturerData, lecturerID, gqlFields)) as any as NarrowedEntity<
        LecturerData,
        typeof gqlFields
      >;
    };
  },
});

export const subscribeLecturerDataByLecturerID = registerSubscription({
  operationName: 'subscribeLecturerDataByID',
  type: () => LecturerData,
  params: [{ name: 'lecturerID', type: () => String }],
  resolve: (lecturerID: string) => {
    return async (gqlFields: BaseQLSelectionSet<LecturerData>) => {
      return watchEntireCollection(LecturerData._collectionName).pipe(
        switchMap(async () => {
          const user = await getCurrentUserServer();
          if (!user?.hasPermission(Permission.EDIT_LECTURERS) && user?._id !== lecturerID)
            throw new Error('You dont have enough permission!');
          gqlFields._id = 1;
          gqlFields._class = 1;
          const result = await loadRawEntityDataByQuery(LecturerData, { lecturerID }, gqlFields);
          return result.at(0) ?? null;
        }),
      );
    };
  },
});
